import { loadFont as loadCaveat } from "@remotion/google-fonts/Caveat";
import { loadFont as loadPoppins } from "@remotion/google-fonts/Poppins";
import { MAP_STYLE_URLS } from "constants/mapStyles";
import mapboxgl, { Map, MercatorCoordinate } from "mapbox-gl";
import React, { useEffect, useRef, useState } from "react";
import {
	AbsoluteFill,
	Composition,
	Easing,
	Img,
	OffthreadVideo,
	Sequence,
	continueRender,
	delayRender,
	getRemotionEnvironment,
	interpolate,
	spring,
	useCurrentFrame,
	useVideoConfig,
} from "remotion";
import { TileGate } from "../TikTokVideoVanilla";
import { cameraGroundPosition, computeCameraPosition } from "../computeCameraPosition";
import ReelOutro from "../reeloutro/ReelOutro";
import { Glyph, TEAL, tealShadow } from "../sequences/houseStyle";
import { isoOf } from "../sequences/introCamera";
import { hashSeed, markerStroke, scribbleLoop } from "../sequences/introDrawing";
import { isVideoMedia, mediaUrl } from "../sequences/reelMedia";
import { warmTiles } from "../tileWarmup";
import {
	BANGKOK_GUIDE,
	CityGuideProps,
	END_HOLD,
	PAN,
	TITLE_LEAD,
	cameraLeg,
	cityGuideFrames,
	cityGuideSchema,
	spotArrive,
	spotStart,
} from "./cityGuideSchema";

const { fontFamily: handwriting } = loadCaveat("normal", { weights: ["700"], subsets: ["latin"] });
const { fontFamily: poppins } = loadPoppins("normal", { weights: ["500", "600", "700"], subsets: ["latin"] });

/** Which Puffy glyph a category gets on its chip. */
const CATEGORY_GLYPH: Record<string, string> = {
	landmark: "landmark",
	food: "restaurant",
	shopping: "shopping",
	museum: "museum",
	coffee: "coffee",
	nightlife: "cocktail",
	beach: "beach",
	nature: "park",
	viewpoint: "camera",
};

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/** The camera over the guide: always the same height and pitch, drifting its heading a little so the map is never a still. */
const cameraFor = (frame: number, props: CityGuideProps) => {
	const { spots, altitude, pitch } = props;
	const leg = cameraLeg(frame, props);
	const a = spots[leg.from];
	const b = spots[leg.to];
	const e = Easing.inOut(Easing.cubic)(leg.t);
	return {
		target: { lng: a.lng + (b.lng - a.lng) * e, lat: a.lat + (b.lat - a.lat) * e },
		altitude,
		pitch,
		bearing: -8 + 10 * Math.sin(frame / 120),
	};
};

export const CityGuide: React.FC<CityGuideProps> = (props) => {
	const frame = useCurrentFrame();
	const { fps, width, height } = useVideoConfig();
	const px = (n: number) => (n * width) / 1080;
	const { spots } = props;
	const n = spots.length;
	const spotsEnd = spotStart(n, props);

	const container = useRef<HTMLDivElement>(null);
	const [handle] = useState(() => delayRender("Loading the city guide map"));
	const released = useRef(false);
	const [map, setMap] = useState<Map | null>(null);
	const built = useRef<{ map: Map; style: string } | null>(null);

	// One map for the life of the composition. It used to be rebuilt whenever the style changed, and
	// removing a Mapbox map with requests still in flight throws inside Mapbox ("reading 'send'"),
	// which Studio shows as an error overlay the moment someone picks another style. A development
	// double mount did the same. So: build once, swap the style on the map that exists.
	useEffect(() => {
		if (!container.current || built.current) return;
		const first = cameraFor(0, props);
		const _map = new mapboxgl.Map({
			container: container.current,
			style: MAP_STYLE_URLS[props.mapStyle],
			interactive: false,
			fadeDuration: 0,
			center: [first.target.lng, first.target.lat],
			zoom: 13,
			pitch: props.pitch,
			// @ts-ignore -- `projection` is a valid option in mapbox-gl v3, missing from these typings.
			projection: "mercator",
			maxTileCacheSize: 2500,
		});
		built.current = { map: _map, style: MAP_STYLE_URLS[props.mapStyle] };
		_map.on("load", () => {
			// Built before its container had a size, like the trip reel's map: correct it before anything
			// reads the transform.
			_map.resize();
			const start = () => {
				setMap(_map);
				if (!released.current) {
					released.current = true;
					continueRender(handle);
				}
			};
			if (!getRemotionEnvironment().isRendering) {
				start();
				return;
			}
			// Walk the guide once before rendering, so a render tab that starts on the fourth place does
			// not arrive there cold. Every place, and the middle of every glide between two.
			const keyframes = spots
				.flatMap((s, i) => (i === 0 ? [s] : [{ ...s, lng: (spots[i - 1].lng + s.lng) / 2, lat: (spots[i - 1].lat + s.lat) / 2 }, s]))
				.map((s) => {
					const ground = cameraGroundPosition(props.pitch, -8, { lng: s.lng, lat: s.lat }, props.altitude);
					return { lng: ground?.lng ?? s.lng, lat: ground?.lat ?? s.lat, altitude: props.altitude, pitch: props.pitch, bearing: -8 };
				});
			warmTiles(_map, keyframes)
				.catch(() => undefined)
				.then(start);
		});
		// Built once; everything else is applied per frame.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const url = MAP_STYLE_URLS[props.mapStyle];
		if (!built.current || built.current.style === url) return;
		built.current.style = url;
		built.current.map.setStyle(url);
	}, [props.mapStyle]);

	const drawn = map as any;
	if (drawn && (drawn.transform?.width !== width || drawn.transform?.height !== height)) drawn.resize();

	if (map) {
		const cam = cameraFor(frame, props);
		const ground = computeCameraPosition(cam.pitch, cam.bearing, cam.target, cam.altitude);
		if (ground) {
			const camera = map.getFreeCameraOptions();
			camera.setPitchBearing(cam.pitch, cam.bearing);
			camera.position = MercatorCoordinate.fromLngLat(ground, cam.altitude);
			map.setFreeCameraOptions(camera);
		}
	}

	const project = (lng: number, lat: number) => {
		if (!map) return [width / 2, height / 2];
		const p = map.project([lng, lat]);
		return isFinite(p.x) && isFinite(p.y) ? [p.x, p.y] : [width / 2, height / 2];
	};

	// ---- the circle -------------------------------------------------------------------------------
	const rx = px(125) * props.circleSize;
	const ry = px(88) * props.circleSize;
	const circle = (i: number, cx: number, cy: number, progress: number, grow = 1, opacity = 1) => {
		if (progress <= 0.001 || opacity <= 0.001) return null;
		const seed = hashSeed(spots[i].name + i);
		const tilt = -14 + (seed % 22);
		const loop = scribbleLoop(cx, cy, rx * grow, ry * grow, tilt, seed, 96, props.circleLooseness);
		const shown = loop.slice(0, Math.max(2, Math.round(progress * (loop.length - 1)) + 1));
		const d = markerStroke(shown, px(props.circleWidth) * Math.sqrt(grow));
		return (
			<g key={`c${i}`} opacity={opacity}>
				<path d={d} fill="rgba(15,62,74,.3)" transform={`translate(0 ${px(3)})`} />
				<path d={d} fill={props.circleColor} />
			</g>
		);
	};

	// Which place is on screen now: the last one whose time has started.
	const upTo = spots.findIndex((_, i) => frame < spotStart(i + 1, props));
	const current = upTo === -1 ? n - 1 : upTo;
	const active = frame >= TITLE_LEAD - 4 ? current : -1;

	// ---- the map layer: dots for every place, circles for the ones visited, the current one ---------
	const marks: React.ReactNode[] = [];
	spots.forEach((s, i) => {
		const [x, y] = project(s.lng, s.lat);
		const arrive = spotArrive(i, props);
		const leaveAt = spotStart(i + 1, props);
		// Every place is a small dot from the start, so the guide reads as a map of where it is going.
		const dotIn = spring({ frame: frame - 6 - i * 3, fps, config: { damping: 12, stiffness: 200 } });
		marks.push(
			<g key={`d${i}`} transform={`translate(${x} ${y}) scale(${dotIn})`}>
				<circle r={px(13)} cy={px(2)} fill="rgba(15,62,74,.3)" />
				<circle r={px(11)} fill="#fff" />
				<circle r={px(7)} fill={props.accentColor} />
			</g>
		);
		const draw = interpolate(frame, [arrive, arrive + 18], [0, 1], { ...clamp, easing: Easing.inOut(Easing.sin) });
		// Once the guide moves on, the circle settles smaller and fainter and stays: the route builds up.
		const settle = interpolate(frame, [leaveAt, leaveAt + PAN], [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const keep = props.keepCircles || i === n - 1 ? 1 : 0;
		const opacity = 1 - settle * (1 - 0.45 * keep);
		marks.push(circle(i, x, y, draw, 1 - 0.4 * settle, frame < arrive ? 0 : opacity));
	});

	// ---- the current place: its name, its card -----------------------------------------------------
	let label: React.ReactNode = null;
	let card: React.ReactNode = null;
	let tether: React.ReactNode = null;
	if (active >= 0) {
		const s = spots[active];
		const [x, y] = project(s.lng, s.lat);
		const arrive = spotArrive(active, props);
		const leaveAt = active < n - 1 ? spotStart(active + 1, props) : spotsEnd + END_HOLD;

		const labelIn = spring({ frame: frame - arrive - 10, fps, config: { damping: 13, stiffness: 170 } });
		// Gone by `leaveAt`: from that frame the next place is the active one and this label is not
		// drawn at all, so a fade that ran past it cut off at half opacity.
		const labelOut = interpolate(frame, [leaveAt - 10, leaveAt], [1, 0], clamp);
		const lv = Math.min(labelIn, labelOut);
		const onLeft = x > width * 0.62;
		const lx = onLeft ? x - rx * 0.95 - px(16) : x + rx * 0.95 + px(16);
		const ly = y + ry * 0.55;
		const hand = props.labelFont === "handwritten";
		label = (
			<g opacity={lv} transform={`translate(${lx} ${ly}) scale(${0.7 + 0.3 * lv})`}>
				{props.numbered ? (
					<g transform={`translate(${onLeft ? -px(26) : px(26)} ${-px(16)})`}>
						<circle r={px(28)} cy={px(2)} fill="rgba(15,62,74,.3)" />
						<circle r={px(27)} fill={props.accentColor} stroke="#fff" strokeWidth={px(4)} />
						<text textAnchor="middle" y={px(11)} fontFamily={poppins} fontWeight={700} fontSize={px(30)} fill="#fff">
							{active + 1}
						</text>
					</g>
				) : null}
				<text
					x={props.numbered ? (onLeft ? -px(66) : px(66)) : 0}
					y={hand ? px(4) : px(0)}
					textAnchor={onLeft ? "end" : "start"}
					fontFamily={hand ? handwriting : poppins}
					fontWeight={700}
					fontSize={hand ? px(66) : px(46)}
					fill={props.labelColor}
					stroke="#fff"
					strokeWidth={px(hand ? 10 : 8)}
					paintOrder="stroke"
					strokeLinejoin="round"
					transform={hand ? `rotate(-4)` : undefined}
				>
					{s.name}
				</text>
			</g>
		);

		// The card: what you see there. Out of the circle, above it, and back into it on the way out.
		const pop = spring({ frame: frame - arrive - 14, fps, config: { damping: 13, stiffness: 150 } });
		const tuck = interpolate(frame, [leaveAt - 14, leaveAt - 2], [0, 1], { ...clamp, easing: Easing.in(Easing.back(1.4)) });
		const q = Math.max(0, pop * (1 - tuck));
		if (q > 0.001) {
			const isCard = props.photoStyle === "card";
			const cw = isCard ? px(340) : px(310);
			const ch = isCard ? (cw * 416) / 368 : cw;
			const margin = px(40);
			const sx = Math.max(margin + cw / 2, Math.min(width - margin - cw / 2, x));
			let top = y - ry - px(34) - ch;
			if (top < px(300)) top = y + ry + px(90);
			const sy = top + ch / 2;
			const cx = x + (sx - x) * q;
			const cy = y + (sy - y) * q;
			const tilt = (active % 2 ? 3 : -3) * q;
			const media = s.media;
			const clip = isVideoMedia(media);
			const mediaEl = clip ? (
				<Sequence from={arrive + 14} layout="none">
					<OffthreadVideo src={mediaUrl(media)} startFrom={Math.round(s.startAt * fps)} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
				</Sequence>
			) : (
				<Img src={mediaUrl(media)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
			);
			const chip = (
				<div
					style={{
						position: "absolute",
						left: px(16),
						top: px(16),
						display: "flex",
						alignItems: "center",
						gap: px(8),
						height: px(48),
						padding: `0 ${px(18)}px 0 ${px(12)}px`,
						borderRadius: px(24),
						background: "#fff",
						color: TEAL,
						fontFamily: poppins,
						fontWeight: 600,
						fontSize: px(22),
						textTransform: "capitalize",
						boxShadow: tealShadow(px(3), px(10), 0.25),
					}}
				>
					<svg width={px(30)} height={px(30)} viewBox={`${-px(15)} ${-px(15)} ${px(30)} ${px(30)}`}>
						<Glyph name={CATEGORY_GLYPH[s.category] || "map-marker"} size={px(30)} color={props.accentColor} />
					</svg>
					{s.category}
				</div>
			);
			card = (
				<div
					style={{
						position: "absolute",
						left: cx - cw / 2,
						top: cy - ch / 2,
						width: cw,
						height: ch,
						transform: `scale(${0.12 + 0.88 * q}) rotate(${tilt}deg)`,
					}}
				>
					<div
						style={{
							width: cw,
							height: isCard ? ch : cw,
							borderRadius: isCard ? px(30) : "50%",
							border: `${px(isCard ? 12 : 10)}px solid #fff`,
							boxSizing: "border-box",
							overflow: "hidden",
							position: "relative",
							background: "#e6eef0",
							boxShadow: tealShadow(px(12), px(34), 0.4),
						}}
					>
						{mediaEl}
						{isCard ? (
							<>
								<AbsoluteFill style={{ background: "linear-gradient(to top, rgba(15,62,74,.78) 0%, rgba(15,62,74,0) 42%)" }} />
								<div
									style={{
										position: "absolute",
										left: px(18),
										right: px(18),
										bottom: px(16),
										color: "#fff",
										fontFamily: poppins,
										fontWeight: 600,
										fontSize: px(27),
										lineHeight: 1.2,
									}}
								>
									{s.note}
								</div>
								{chip}
							</>
						) : null}
					</div>
					{!isCard ? (
						<div style={{ position: "absolute", left: -px(100), right: -px(100), top: cw - px(18), display: "flex", justifyContent: "center" }}>
							<div
								style={{
									padding: `${px(12)}px ${px(22)}px`,
									borderRadius: px(30),
									background: "#fff",
									color: TEAL,
									fontFamily: poppins,
									fontWeight: 600,
									fontSize: px(26),
									boxShadow: tealShadow(px(4), px(16)),
									maxWidth: px(420),
									textAlign: "center",
								}}
							>
								{s.note}
							</div>
						</div>
					) : null}
				</div>
			);
			// A dotted line from the circle to the card, the same stitch as the trip intro's route.
			const edgeY = sy > y ? y + ry : y - ry;
			const cardEdgeY = sy > y ? cy - (ch / 2) * q : cy + (ch / 2) * q;
			tether = (
				<line
					x1={x}
					y1={edgeY}
					x2={cx}
					y2={cardEdgeY}
					stroke="#fff"
					strokeOpacity={0.9 * Math.min(1, q * 1.5)}
					strokeWidth={px(6)}
					strokeLinecap="round"
					strokeDasharray={`0 ${px(14)}`}
				/>
			);
		}
	}

	// ---- title and counter --------------------------------------------------------------------------
	const flag = isoOf(props.country)?.toLowerCase();
	const titleIn = spring({ frame: frame + 6, fps, config: { damping: 14, stiffness: 150 } });
	const titleOut = interpolate(frame, [TITLE_LEAD - 6, TITLE_LEAD + 10], [0, 1], { ...clamp, easing: Easing.in(Easing.cubic) });
	const underline = interpolate(frame, [8, 28], [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
	const counterIn = spring({ frame: frame - TITLE_LEAD - 6, fps, config: { damping: 14, stiffness: 160 } });
	const counterOut = interpolate(frame, [spotsEnd + END_HOLD - 6, spotsEnd + END_HOLD + 6], [1, 0], clamp);
	const flagBadge = (size: number) =>
		flag ? (
			<div
				style={{
					width: size,
					height: size,
					borderRadius: "50%",
					border: `${px(4)}px solid #fff`,
					boxSizing: "border-box",
					overflow: "hidden",
					flexShrink: 0,
					boxShadow: tealShadow(px(3), px(12), 0.3),
				}}
			>
				<Img src={`https://flagcdn.com/w320/${flag}.png`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
			</div>
		) : null;

	return (
		<AbsoluteFill style={{ background: "#0f3e4a" }}>
			<AbsoluteFill ref={container} />
			<AbsoluteFill
				style={{
					opacity: 1 - 0.55 * titleOut,
					background: "linear-gradient(to bottom, rgba(15,62,74,.66) 0%, rgba(15,62,74,.2) 30%, rgba(15,62,74,0) 46%)",
				}}
			/>
			<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
				{marks}
				{tether}
				{label}
			</svg>
			{card}

			{titleOut < 1 ? (
				<div
					style={{
						position: "absolute",
						top: px(250),
						left: px(72),
						right: px(72),
						fontFamily: poppins,
						color: "#fff",
						opacity: Math.min(1, titleIn * 1.5) * (1 - titleOut),
						transform: `translateY(${px(40) * (1 - titleIn) - px(70) * titleOut}px)`,
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: px(14) }}>
						{flagBadge(px(54))}
						<div
							style={{
								fontSize: px(28),
								fontWeight: 700,
								letterSpacing: px(5),
								textTransform: "uppercase",
								textShadow: `0 ${px(1)}px ${px(10)}px rgba(15,62,74,.4)`,
							}}
						>
							{props.kicker}
						</div>
					</div>
					<div
						style={{
							position: "relative",
							display: "inline-block",
							marginTop: px(8),
							fontSize: px(props.title.length > 12 ? 118 : 160),
							fontWeight: 700,
							lineHeight: 1.05,
							letterSpacing: "-0.02em",
							textShadow: `0 ${px(2)}px ${px(18)}px rgba(15,62,74,.4)`,
						}}
					>
						{props.title}
						{/* The website's accent: one green underline, here in the circle's colour, so title and map agree. */}
						<div
							style={{
								position: "absolute",
								left: 0,
								bottom: px(6),
								height: px(14),
								width: `${100 * underline}%`,
								borderRadius: px(7),
								background: props.circleColor,
							}}
						/>
					</div>
				</div>
			) : null}

			{counterIn > 0.01 && counterOut > 0 ? (
				<div
					style={{
						position: "absolute",
						top: px(220),
						left: px(56),
						display: "flex",
						alignItems: "center",
						gap: px(12),
						height: px(70),
						padding: `0 ${px(28)}px 0 ${px(10)}px`,
						borderRadius: px(35),
						background: "#fff",
						fontFamily: poppins,
						boxShadow: tealShadow(px(6), px(20), 0.25),
						opacity: counterOut,
						transform: `translateY(${px(-30) * (1 - counterIn)}px) scale(${0.8 + 0.2 * counterIn})`,
						transformOrigin: "left center",
					}}
				>
					{flagBadge(px(50))}
					<span style={{ fontSize: px(30), fontWeight: 700, color: TEAL }}>{props.title}</span>
					<span style={{ fontSize: px(30), fontWeight: 600, color: props.accentColor }}>
						{Math.max(1, active + 1)}/{n}
					</span>
				</div>
			) : null}

			{props.showOutro ? (
				<Sequence from={spotsEnd + END_HOLD}>
					<ReelOutro
						routeColor={props.accentColor}
						// The outro draws a trip as legs with a photo on each stop; a guide is the same thing on a
						// smaller scale, so hand it the places as straight legs and their stills as the photos.
						reelData={spots.map((s, i) => ({
							path:
								i < n - 1
									? [
											[s.lng, s.lat],
											[spots[i + 1].lng, spots[i + 1].lat],
										]
									: [],
							photos: [s.poster || s.media],
						}))}
						countries={[{ name: props.country }]}
						tripName={props.title}
						stats={`${n} spots`}
					/>
				</Sequence>
			) : null}

			{/* The map layer holds each frame until its tiles have arrived, as the trip reel's does. */}
			<TileGate map={map} />
		</AbsoluteFill>
	);
};

/** Registered in the bundle's Root beside the trip reel and the social compositions. */
export const CityGuideComposition: React.FC = () => (
	<Composition
		id="city-guide"
		component={CityGuide}
		schema={cityGuideSchema}
		defaultProps={BANGKOK_GUIDE}
		fps={30}
		width={1080}
		height={1920}
		durationInFrames={cityGuideFrames(BANGKOK_GUIDE)}
		calculateMetadata={({ props }) => ({ durationInFrames: cityGuideFrames(props) })}
	/>
);
